import { msg } from "@lingui/core/macro";
import { type Component, createMemo, For, Show } from "solid-js";
import { Keyboard } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { useCommandCatalog, useShortcuts } from "@fleet/shortcuts";

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DisplayBinding {
  steps: { keys: string[] }[];
}

interface DisplayedShortcut {
  description: string;
  bindings: DisplayBinding[];
}

interface DisplayedCategory {
  title: string;
  shortcuts: DisplayedShortcut[];
}

/**
 * Shortcuts palette (URL-addressable: `?modal=shortcuts`). Mounts on top of
 * Kobalte base `Dialog` to inherit platform CONTRACT: role
 * `dialog` with accessible name ("Keyboard Shortcuts"), close on Escape and —
 * of CC-19 — browser back-dismiss via `useBackDismiss` (back button
 * closes overlay and cleans URL instead of navigating), without bouncing.
 */
export const ShortcutsModal: Component<ShortcutsModalProps> = (props) => {
  const { t, tDynamic } = useI18n();
  const { registry, format, platform } = useShortcuts();
  const catalog = useCommandCatalog();

  const categories = createMemo<DisplayedCategory[]>(() => {
    const groups = catalog();
    const result: DisplayedCategory[] = [];
    for (const { category, commands } of groups) {
      const shortcuts: DisplayedShortcut[] = [];
      for (const cmd of commands) {
        const bindings = registry.bindingsFor(cmd.id);
        if (bindings.length === 0) {
          continue;
        }
        // Filter bindings by platform-availability (engine already filters at
        // dispatch time but the help modal should mirror what's reachable).
        const reachable = bindings.filter((b) => {
          if (b.platforms && !b.platforms.includes(platform.id)) {
            return false;
          }
          if (platform.reservedBindings.has(b.keys)) {
            return false;
          }
          return true;
        });
        if (reachable.length === 0) {
          continue;
        }
        shortcuts.push({
          // DYNAMIC keys declared with `msg` in `lib/shortcuts/messages.ts`.
          description: tDynamic(cmd.titleKey),
          bindings: reachable.map((b) => format(b.keys)),
        });
      }
      if (shortcuts.length > 0) {
        result.push({ title: tDynamic(category), shortcuts });
      }
    }
    return result;
  });

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
      role="dialog"
      contentClass="max-w-3xl max-h-[85vh] flex flex-col"
      dragToDismissDisabled
    >
      <div class="flex items-center gap-3 border-b border-border/20 px-6 py-4 shrink-0">
        <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/20 text-primary">
          <Keyboard class="h-5 w-5" />
        </div>
        <DialogTitle class="text-lg font-bold">{t(msg`Keyboard Shortcuts`)}</DialogTitle>
      </div>

      <div class="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 scrollbar-thin md:grid-cols-2">
        <For each={categories()}>
          {(category) => (
            <div class="space-y-3">
              <h3 class="border-b border-border/10 px-1 pb-1.5 text-xs font-bold uppercase tracking-wider text-primary/80">
                {category.title}
              </h3>
              <div class="space-y-2">
                <For each={category.shortcuts}>
                  {(shortcut) => (
                    <div class="flex items-center justify-between rounded-lg px-2 py-1.5 transition-colors hover:bg-muted/5">
                      <span class="pr-4 text-xs font-medium text-muted-foreground">
                        {shortcut.description}
                      </span>
                      <div class="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <For each={shortcut.bindings}>
                          {(binding, bIdx) => (
                            <>
                              <Show when={bIdx() > 0}>
                                <span class="px-0.5 text-[9px] text-muted-foreground/30">/</span>
                              </Show>
                              <div class="flex items-center gap-1">
                                <For each={binding.steps}>
                                  {(step, sIdx) => (
                                    <>
                                      <Show when={sIdx() > 0}>
                                        <span class="px-0.5 text-[10px] font-semibold text-muted-foreground/40">
                                          then
                                        </span>
                                      </Show>
                                      <div class="flex items-center gap-0.5">
                                        <For each={step.keys}>
                                          {(key, kIdx) => (
                                            <>
                                              <Show when={kIdx() > 0}>
                                                <span class="px-0.5 text-[10px] font-semibold text-muted-foreground/40">
                                                  +
                                                </span>
                                              </Show>
                                              <kbd class="inline-flex h-5 items-center justify-center rounded border border-border/60 bg-background px-1.5 font-mono text-[9px] font-bold text-foreground/80 shadow-sm">
                                                {key}
                                              </kbd>
                                            </>
                                          )}
                                        </For>
                                      </div>
                                    </>
                                  )}
                                </For>
                              </div>
                            </>
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </div>
          )}
        </For>
      </div>

      <div class="flex shrink-0 items-center justify-between border-t border-border/20 bg-muted/10 px-6 py-4 text-[10px] text-muted-foreground">
        <span>{t(msg`Press '?' on any screen to open this guide.`)}</span>
        <span>{t(msg`Close with 'Esc' or click outside.`)}</span>
      </div>
    </Dialog>
  );
};
